import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { aud } from '@/utils/format';
import { artistOutstanding } from '@/domain/payoutFlow';
import { ALERT_BANDS, leadThresholdBand } from '@/data/reportingTargets';
import {
  targetWeek,
  dayKeyOf,
  rescheduleEvents,
  datelessDepositCohort,
  reviewsDelta,
  newClientsByArtistWeek,
} from '@/domain/reportingEngine';
import type { StudioId } from '@/domain/types';

/**
 * Threshold alerts (Prompt G, Stage 5, item 19). On sim-clock day rollover, a
 * single channel ping fires for each breached band - reschedules above band, the
 * dateless deposit cohort above N, reviews below target, or an artist stuck in the
 * low new-client band. Dedupe uses the simulation store's `firedEvents` log
 * (id `alert:<key>@<dayKey>`), so calling this repeatedly the same sim day is a
 * no-op: exactly once per breach per day.
 *
 * `// ASSUMPTION: bands are placeholders in reportingTargets.ts; the client sets
 * the real thresholds.`
 */
export interface AlertBreach {
  /** Stable dedupe key, e.g. 'dateless:coburg' or 'lead:asher'. */
  key: string;
  studioId?: StudioId;
  /** The channel ping text. */
  body: string;
}

/** "Sat 2 May" — the work day an unpaid envelope belongs to. */
const dayLabel = (dayIso: string): string =>
  format(new Date(`${dayIso}T12:00:00+10:00`), 'EEE d MMM');

/** Compute the day's threshold breaches. Pure read - no writes, no pings. */
export function computeBreaches(nowIso: string): AlertBreach[] {
  const { studios, artists } = useBrainStore.getState();
  // Canonical operational week = the target week (Thu-Tue), so an alert's "this
  // week" matches the Targets tab exactly.
  const days = targetWeek(nowIso).days;
  const breaches: AlertBreach[] = [];

  for (const s of Object.values(studios)) {
    if (s.archived) continue;
    const reschedules = rescheduleEvents(days, s.id).count;
    if (reschedules > ALERT_BANDS.reschedulesAbove) {
      breaches.push({
        key: `reschedules:${s.id}`,
        studioId: s.id,
        body: `⚠️ ${s.name}: ${reschedules} reschedules this week - above the ${ALERT_BANDS.reschedulesAbove} band. Check the Levers tab for who leaked.`,
      });
    }
    const dateless = datelessDepositCohort(s.id).count;
    if (dateless > ALERT_BANDS.datelessAbove) {
      breaches.push({
        key: `dateless:${s.id}`,
        studioId: s.id,
        body: `⚠️ ${s.name}: ${dateless} paid deposits with no date booked - above the ${ALERT_BANDS.datelessAbove} band. Work them from the queue.`,
      });
    }
    const reviews = reviewsDelta(days, s.id);
    if (!reviews.meetsTarget) {
      breaches.push({
        key: `reviews:${s.id}`,
        studioId: s.id,
        body: `⚠️ ${s.name}: ${reviews.thisWeek}/${reviews.target} reviews this week - below target.`,
      });
    }
  }

  // An envelope nobody could pay is a money breach, not a scheduling one: an
  // artist is paid whole or not at all, so a deferred day sits there until the
  // drawer can cover it. Two days is a quiet patch; past the band it is a pattern
  // somebody has to act on.
  for (const artist of Object.values(artists)) {
    if (artist.archived) continue;
    const stale = artistOutstanding(artist.studioId, artist.id, nowIso).filter((d) => d.escalated);
    if (stale.length === 0) continue;
    const owed = stale.reduce((sum, d) => sum + d.envelopeAud, 0);
    const oldest = stale[0]!;
    breaches.push({
      key: `payout-arrears:${artist.id}`,
      studioId: artist.studioId,
      body: `⚠️ ${artist.displayName}: ${aud(owed)} still owed — ${dayLabel(oldest.dayIso)} unpaid ${oldest.ageDays} days. Pay it before today's envelopes.`,
    });
  }

  const newClients = newClientsByArtistWeek(days);
  for (const a of Object.values(artists)) {
    if (a.archived || a.isApprentice) continue;
    const n = newClients[a.id] ?? 0;
    if (leadThresholdBand(n) === 'meet') {
      breaches.push({
        key: `lead:${a.id}`,
        studioId: a.studioId,
        body: `⚠️ ${a.displayName}: only ${n} new clients this week - in the lowest lead band.`,
      });
    }
  }

  return breaches;
}

/**
 * Fire a channel ping for each un-fired breach today. Returns how many pings were
 * posted. Idempotent per (breach, day): safe to call on every clock change.
 */
export function runThresholdAlerts(nowIso: string = useSimulationStore.getState().clockIso): number {
  const dayKey = dayKeyOf(nowIso);
  let fired = 0;
  for (const breach of computeBreaches(nowIso)) {
    const id = `alert:${breach.key}@${dayKey}`;
    if (useSimulationStore.getState().firedEvents.some((e) => e.eventId === id)) continue;
    const brain = useBrainStore.getState();
    const channel = autoPingChannelFor('threshold-breach', breach.studioId, brain.internalComms);
    if (!channel) continue; // no channel to post to; retry on a later call
    brain.addAutoPing(channel, breach.body, { timestamp: nowIso });
    useSimulationStore.getState().recordFired(id);
    fired += 1;
  }
  return fired;
}
